
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Heart, MapPin, Sparkles, Check } from "lucide-react";
import { careNeeds } from "@/components/waitlist/schema";

// County options for location step
const countyOptions = [
  "Dublin", "Cork", "Galway", "Limerick", "Waterford", "Kerry",
  "Wicklow", "Kildare", "Meath", "Louth", "Wexford", "Tipperary",
  "Clare", "Mayo", "Donegal", "Sligo", "Kilkenny", "Laois",
  "Offaly", "Westmeath", "Longford", "Roscommon", "Leitrim",
  "Cavan", "Monaghan", "Carlow",
];

const timeframeOptions = [
  { id: "immediate", label: "Within 1 month", emoji: "🔴" },
  { id: "soon", label: "1–3 months", emoji: "🟡" },
  { id: "planning", label: "3–6 months", emoji: "🟢" },
  { id: "researching", label: "Just researching", emoji: "🔵" },
];

const priorityOptions = [
  { id: "location", label: "Close to family" },
  { id: "cost", label: "Affordable pricing" },
  { id: "quality", label: "High HIQA rating" },
  { id: "specialization", label: "Specialist care" },
  { id: "fair_deal", label: "Fair Deal approved" },
  { id: "availability", label: "Immediate availability" },
];

export interface OnboardingData {
  careNeeds: string[];
  timeframe: string;
  counties: string[];
  priorities: string[];
}

interface OnboardingWizardProps {
  firstName?: string;
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
}

export function OnboardingWizard({ firstName, onComplete, onSkip }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    careNeeds: [],
    timeframe: "",
    counties: [],
    priorities: [],
  });

  const steps = [
    { title: "Who needs care?", icon: Heart, description: "Help us understand the type of care needed" },
    { title: "Where are you looking?", icon: MapPin, description: "Select your preferred counties" },
    { title: "What matters most?", icon: Sparkles, description: "Choose your top priorities" },
  ];

  const progress = ((step + 1) / steps.length) * 100;

  const toggleArrayItem = (field: keyof OnboardingData, value: string) => {
    setData(prev => {
      const arr = prev[field] as string[];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  };

  const canProceed = () => {
    if (step === 0) return data.careNeeds.length > 0 && data.timeframe !== "";
    if (step === 1) return data.counties.length > 0;
    if (step === 2) return data.priorities.length > 0;
    return false;
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete(data);
    }
  };

  const StepIcon = steps[step].icon;

  return (
    <Card className="max-w-2xl mx-auto p-6 md:p-8 border-0 shadow-xl bg-background">
      {/* Header */}
      <div className="text-center mb-6">
        <p className="text-sm text-muted-foreground mb-1">
          {firstName ? `Welcome, ${firstName}!` : "Welcome!"} Let's personalise your experience
        </p>
        <Progress value={progress} className="h-1.5 mt-3" />
        <p className="text-xs text-muted-foreground mt-2">Step {step + 1} of {steps.length}</p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {/* Step title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <StepIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{steps[step].title}</h2>
              <p className="text-sm text-muted-foreground">{steps[step].description}</p>
            </div>
          </div>

          {/* Step 0: Care needs + timeframe */}
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">Type of care needed</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {careNeeds.map(need => (
                    <button
                      key={need.id}
                      onClick={() => toggleArrayItem("careNeeds", need.id)}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                        data.careNeeds.includes(need.id)
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-background border-border text-foreground hover:border-primary/50"
                      }`}
                    >
                      {data.careNeeds.includes(need.id) && <Check className="inline w-3.5 h-3.5 mr-1" />}
                      {need.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">How soon do you need care?</label>
                <div className="grid grid-cols-2 gap-2">
                  {timeframeOptions.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setData(prev => ({ ...prev, timeframe: opt.id }))}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                        data.timeframe === opt.id
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-background border-border text-foreground hover:border-primary/50"
                      }`}
                    >
                      <span className="mr-1.5">{opt.emoji}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Counties */}
          {step === 1 && (
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">
                Select preferred counties <span className="text-muted-foreground font-normal">(pick as many as you'd like)</span>
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[320px] overflow-y-auto pr-1">
                {countyOptions.map(county => (
                  <button
                    key={county}
                    onClick={() => toggleArrayItem("counties", county.toLowerCase())}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      data.counties.includes(county.toLowerCase())
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-background border-border text-foreground hover:border-primary/50"
                    }`}
                  >
                    {data.counties.includes(county.toLowerCase()) && <Check className="inline w-3 h-3 mr-1" />}
                    {county}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Priorities */}
          {step === 2 && (
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">
                What's most important to you? <span className="text-muted-foreground font-normal">(select all that apply)</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {priorityOptions.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => toggleArrayItem("priorities", opt.id)}
                    className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all text-left ${
                      data.priorities.includes(opt.id)
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-background border-border text-foreground hover:border-primary/50"
                    }`}
                  >
                    {data.priorities.includes(opt.id) && <Check className="inline w-3.5 h-3.5 mr-1.5" />}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="flex items-center justify-between mt-8 pt-4 border-t">
        <div>
          {step > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
              Skip for now
            </Button>
          )}
        </div>

        <Button
          onClick={handleNext}
          disabled={!canProceed()}
          className="bg-primary hover:bg-primary/90"
        >
          {step === steps.length - 1 ? "Get Recommendations" : "Continue"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </Card>
  );
}
